import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth-context.js';
import { DEMO_DASHBOARD_SUMMARY, toEnvelope, useDemoOrLive } from '../demo/index.js';

export interface DashboardSummary {
  licenseId: string;
  orgsCount: number | null;
  teamsCount: number | null;
  scenariosCount: number | null;
  kpiTypesCount: number | null;
  fetchedAt: string;
}

/**
 * Per-license dashboard summary. Calls `/v1/dashboard/summary` on
 * app-api which mints a per-license engine JWT and proxies to
 * `/engine/v1/status/orgs`. The customer JWT in sessionStorage
 * authorizes the SPA → app-api hop; license id comes from the
 * verified claim, not the URL.
 */
export function useDashboardSummary(): {
  data?: DashboardSummary;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['dashboard', 'summary', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<DashboardSummary>('/v1/dashboard/summary'),
    enabled: !!session,
    staleTime: 30_000,
  });
  // Demo mode swaps to the bundled DashboardSummary fixture; live
  // mode passes the TanStack-Query result through unchanged.
  const env = useDemoOrLive(DEMO_DASHBOARD_SUMMARY, toEnvelope(q));
  return {
    data: env.data,
    isLoading: env.isLoading,
    isFetching: env.isFetching ?? false,
    error: env.error,
    refetch: env.refetch ?? (() => undefined),
  };
}

/**
 * One-stop "refresh everything visible on the dashboard" — fires a
 * coordinated re-fetch on the summary + scenarios list + system-log
 * feed by invalidating their cache keys. The trigger in the
 * dashboard header uses this; each panel's per-panel refresh button
 * still works for narrower refetches.
 */
export function useRefreshDashboard(): () => void {
  const { session } = useAuth();
  const qc = useQueryClient();
  const licenseId = session?.licenseId ?? 'anon';
  return () => {
    void qc.invalidateQueries({ queryKey: ['dashboard', 'summary', licenseId] });
    void qc.invalidateQueries({ queryKey: ['scenarios', 'list', licenseId] });
    void qc.invalidateQueries({ queryKey: ['logs', 'system', licenseId] });
  };
}
